import React, { useState } from 'react';
import Accordion from '../../../../Accordion';
import {
  Database,
  Calculator,
  TrendingUp,
  Info,
  Zap,
  BarChart3
} from 'lucide-react';

const TransmissionLineDocumentationAccordion = () => {
  
   const [docAccordionKey, setDocAccordionKey] = useState(null);
   
  return (
    <div className="mt-6 w-full min-w-0 bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header — OverviewDocumentationAccordion §5 / overview-tab-mobile-responsive-changes.md */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 md:px-6 lg:px-8 py-4 md:py-5 border-b border-gray-200">
        <div className="flex items-center gap-3 md:gap-4">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Info className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-base md:text-lg lg:text-xl font-semibold text-gray-700">
              Data Sources & Methodology
            </h3>
            <p className="text-sm md:text-base text-gray-500 mt-0.5">
              Grid infrastructure, generation units, and demand clustering
            </p>
          </div>
        </div>
      </div>

      {/* Accordion */}
      <Accordion activeKey={docAccordionKey} onSelect={(k) => setDocAccordionKey(k)} className="border-0">
        {/* Demand Cluster */}
        <Accordion.Item eventKey="0" className="border-0 border-b border-gray-100">
          <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-orange-100 rounded-lg">
                <TrendingUp className="h-4 w-4 text-orange-600" />
              </div>
              <div className="text-left">
                <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">
                  Demand Cluster (Map - Left)
                </h4>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">
                  Population-based demand centers and regions
                </p>
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Database className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Data Source</p>
                  <p className="text-xs lg:text-sm text-gray-700">World cities database (population data).</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calculator className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Processing</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    Cities are clustered into demand centers using Voronoi tessellation. The number of clusters is
                    user-configurable (e.g., 12 shown), and each color represents a distinct demand region.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Note</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    Clustering aggregates thousands of cities into manageable demand centers based on proximity and
                    population, enabling realistic grid representation while keeping computations tractable.
                  </p>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        {/* Generation */}
        <Accordion.Item eventKey="1" className="border-0 border-b border-gray-100">
          <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-green-100 rounded-lg">
                <BarChart3 className="h-4 w-4 text-green-600" />
              </div>
              <div className="text-left">
                <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">
                  Generation (Map - Right)
                </h4>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">Power plants mapped by fuel type</p>
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Database className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Data Source</p>
                  <p className="text-xs lg:text-sm text-gray-700">GEM (Global Energy Monitor).</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calculator className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Processing</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    Operating power plants are mapped by location, with colors representing different fuel types.
                  </p>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        {/* Transmission Lines */}
        <Accordion.Item eventKey="2" className="border-0">
          <Accordion.Header className="px-4 md:px-6 lg:px-8 py-4 md:py-5 hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-3 w-full">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Zap className="h-4 w-4 text-purple-600" />
              </div>
              <div className="text-left">
                <h4 className="text-sm md:text-base lg:text-lg font-semibold text-gray-900 mb-0">
                  Transmission Lines (Both Maps)
                </h4>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5 mb-0">High-voltage network visualization</p>
              </div>
            </div>
          </Accordion.Header>
          <Accordion.Body className="px-4 md:px-6 lg:px-8 pb-4 md:pb-5">
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <Database className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Data Source</p>
                  <p className="text-xs lg:text-sm text-gray-700">OpenStreetMap (OSM).</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Calculator className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Processing</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    Extracted and processed using a PyPSA-based pipeline to display the high-voltage transmission
                    network.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Note</p>
                  <p className="text-xs lg:text-sm text-gray-700">
                    PyPSA (Python for Power System Analysis) is an open-source framework for power system modeling. The
                    pipeline extends it for improved OSM data extraction and validation.
                  </p>
                </div>
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </div>
  );
};

export default TransmissionLineDocumentationAccordion;
